import { useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DefaultOptionType } from 'antd/es/select';
import { useDebounce } from '@hooks';
import { GetItems } from '@services';
import {
  IFilterParameter,
  IItemsWithCount,
  KeyValueRecord,
  SortOrder,
} from '@rasayi-workspace/shared';
import { BASE_QUERY_OPTIONS } from '@constants';

interface SearchableDropdownProps<T> {
  mode?: 'multiple' | 'tags';
  keyProp: string;
  valueProp: string;
  queryKey: string[];
  queryFnParams: {
    apiRoute: string;
    relations: string[];
    fields: string[];
    filter: IFilterParameter[]; // ✅ corrected type
    predefinedFilters: IFilterParameter[];
  };
  onSelectionChange?: (
    selectedValue: string | string[],
    option: DefaultOptionType | DefaultOptionType[],
    items: T[]
  ) => void;
  defaultValue?: string;
  defaultMultiValue?: string[];
  sortBy?: SortOrder;
  enabled?: boolean;
}

export function NewSearchableDropdown<T>({
                                           mode,
                                           keyProp,
                                           valueProp,
                                           queryKey,
                                           queryFnParams: { apiRoute, relations, fields, filter, predefinedFilters },
                                           onSelectionChange = () => {},
                                           defaultValue,
                                           defaultMultiValue,
                                           sortBy,
                                           enabled = true,
                                         }: SearchableDropdownProps<T>): JSX.Element {
  const [items, setItems] = useState<T[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedValue = useDebounce(searchTerm, 500);
  const [spinner, setSpinner] = useState<boolean>(false);

  const { refetch } = useQuery<IItemsWithCount<T>, AxiosError>({
    ...BASE_QUERY_OPTIONS,
    queryKey,
    queryFn: () =>
      GetItems<T>(
        apiRoute,
        relations,
        fields,
        [
          [
            ...predefinedFilters,
            ...filter.map(f => ({
              ...f,
              value: f.value ?? debouncedValue,
            }))
          ]
        ],

        sortBy
      ),
    enabled,
    onSuccess: (res) => setItems(res.items),
    onError: () => setItems([]),
  });

  useEffect(() => {
    if (enabled) {
      setSpinner(false);
      refetch();
    }
  }, [debouncedValue, enabled]);

  const filteredItems = items.filter(
    (item) =>
      (item as KeyValueRecord)[keyProp] !== null &&
      (item as KeyValueRecord)[keyProp] !== undefined
  );

  return (
    <Select
      mode={mode}
      value={defaultMultiValue || undefined}
      defaultValue={defaultMultiValue || undefined}
      autoClearSearchValue={false}
      showSearch
      searchValue={searchTerm}
      optionFilterProp="children"
      onChange={(value, option) => onSelectionChange(value, option, items)}
      onSearch={(value: string) => {
        setSearchTerm(value);
        setSpinner(true);
      }}
      notFoundContent={
        spinner ? <Spin className="flex justify-center p-5" size="small" /> : undefined
      }
      style={{ width: '100%' }}
    >
      {filteredItems.map((item: T, index: number) => {
        const itemKey = (item as KeyValueRecord)[keyProp];
        const itemLabel = (item as KeyValueRecord)[valueProp];
        return (
          <Select.Option key={itemKey ?? `fallback-${index}`} value={itemKey}>
            {itemLabel || 'Unnamed'}
          </Select.Option>
        );
      })}
    </Select>
  );
}
